/**
 * @file SuitabilityProfile.jsx
 * @description Manages new user setup and profile creation
 * 
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 * 
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 * 
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 * 
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 * 
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 * 
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import React, { useEffect, useState } from 'react'
import { Grid } from '@mui/material';
import SelectInput from '../../../components/forms/SelectInput';

function SuitabilityProfile({ sendSuitabilityProfile, submitted }) {

    const timeHorizons = [
        {
            text: "Longest",
            value: "LONGEST"
        },
        {
            text: "Medium",
            value: "AVERAGE"
        },
        {
            text: "Shortest",
            value: "SHORT"
        }
    ]

    const liquidityNeeds = [
        {
            text: "Very Important",
            value: "VERY_IMPORTANT"
        },
        {
            text: "Somewhat Important",
            value: "SOMEWHAT_IMPORTANT"
        },
        {
            text: "Not important",
            value: "NOT_IMPORTANT"
        }
    ]

    const emptySuitabilityProfile = {
        "timeHorizon": "",
        "liquidityNeeds": ""
    };

    const [suitabilityProfile, setSuitabilityProfile] = useState(emptySuitabilityProfile);

    const getErrors = () => {
        if (!submitted) return {};
        const errors = {};
        if (!suitabilityProfile.timeHorizon) errors.timeHorizon = "Time horizon is required";
        if (!suitabilityProfile.liquidityNeeds) errors.liquidityNeeds = "Liquidity needs is required";
        return errors;
    };

    const errors = getErrors();

    const handleChange = (field, value) => {
        setSuitabilityProfile(prevState => ({
            ...prevState,
            [field]: value
        }));
    };

    useEffect(() => {
        sendSuitabilityProfile(suitabilityProfile)
    }, [suitabilityProfile])

    return (
        <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
            <Grid item xs={12} sm={6}>
                <div className="form-field">
                    <SelectInput
                        customInput={{
                            label: "Time Horizon",
                            name: "timeHorizon",
                            value: suitabilityProfile.timeHorizon,
                            action: e => handleChange('timeHorizon', e.target.value),
                            tooltipBool: false,
                            required: true,
                            error: errors.timeHorizon,
                        }}
                        data={timeHorizons}
                    ></SelectInput>
                </div>
            </Grid>
            <Grid item xs={12} sm={6}>
                <div className="form-field">
                    <SelectInput
                        customInput={{
                            label: "Liquidity needs",
                            name: "liquidityNeeds",
                            value: suitabilityProfile.liquidityNeeds,
                            action: e => handleChange('liquidityNeeds', e.target.value),
                            tooltipBool: false,
                            required: true,
                            error: errors.liquidityNeeds,
                        }}
                        data={liquidityNeeds}
                    ></SelectInput>
                </div>
            </Grid>
        </Grid>
    )
}

export default SuitabilityProfile